<div class="trust-page font-poppins text-white">
  <div class="trust-overlay px-4 py-10 sm:px-6 sm:py-14">
    <div class="mx-auto max-w-3xl">
      <header class="mb-10 text-center sm:text-left">
        <img
          src="assets/aisslo-white.webp"
          alt="Aisslo"
          class="mx-auto mb-5 h-10 w-auto sm:mx-0 sm:h-12"
        />
        <p class="text-sm font-medium uppercase tracking-widest text-[#9ed3ff]">
          Trust Center
        </p>
        <h1 class="mt-2 text-3xl font-semibold sm:text-4xl">
          Security &amp; Data Practices
        </h1>
        <p class="mt-3 max-w-2xl text-sm leading-relaxed text-white/75 sm:text-base">
          We built Aisslo to help teams stay ahead of certificate risk. This page
          explains how we protect your data and what we do — and do not — store.
        </p>
      </header>

      <section
        class="mb-6 rounded-xl border border-white/15 bg-[#0b1230]/85 p-6 shadow-xl sm:p-8"
      >
        <h2 class="text-xl font-semibold text-white">Security Overview</h2>
        <div class="mt-4 space-y-4 text-sm leading-relaxed text-white/80 sm:text-base">
          <div>
            <h3 class="mb-1 text-sm font-medium uppercase tracking-wide text-[#9ed3ff]">
              What Aisslo does
            </h3>
            <p>
              Aisslo monitors SSL certificate metadata and helps organizations track
              certificate expiration risks. We surface visibility into your certificate
              landscape so your team can act before outages or compliance issues occur.
            </p>
          </div>
          <div>
            <h3 class="mb-1 text-sm font-medium uppercase tracking-wide text-[#9ed3ff]">
              Security philosophy
            </h3>
            <p>
              Security is built into how we operate — not added as an afterthought. We
              collect only what is needed to deliver certificate monitoring, protect data
              in transit and at rest, and keep sensitive cryptographic material out of our
              systems entirely.
            </p>
          </div>
        </div>
      </section>

      <section
        class="mb-6 rounded-xl border border-white/15 bg-[#0b1230]/85 p-6 shadow-xl sm:p-8"
      >
        <h2 class="text-xl font-semibold text-white">Data Handling</h2>
        <p class="mt-3 text-sm leading-relaxed text-white/75 sm:text-base">
          We are transparent about what enters our platform. Certificate monitoring does
          not require access to your private keys.
        </p>

        <div class="mt-6 grid gap-4 sm:grid-cols-2">
          <div class="rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-5">
            <h3 class="flex items-center gap-2 text-base font-semibold text-emerald-300">
              <span aria-hidden="true">✓</span>
              Stored
            </h3>
            <ul class="mt-3 space-y-2 text-sm text-white/85 sm:text-base">
              <li>Certificate metadata</li>
              <li>Domain and expiration details</li>
              <li>Account and organization information</li>
            </ul>
          </div>

          <div class="rounded-lg border border-red-500/40 bg-red-950/20 p-5">
            <h3 class="flex items-center gap-2 text-base font-semibold text-red-300">
              <span aria-hidden="true">✕</span>
              Not stored
            </h3>
            <ul class="mt-3 space-y-2 text-sm text-white/85 sm:text-base">
              <li class="font-semibold text-white">Private keys</li>
              <li>Certificate signing requests (CSRs)</li>
              <li>Passphrases or key material</li>
            </ul>
            <p class="mt-4 text-sm leading-relaxed text-white/70">
              Your private keys never leave your infrastructure. Aisslo does not request,
              transmit, or retain them.
            </p>
          </div>
        </div>
      </section>

      <section
        class="mb-6 rounded-xl border border-white/15 bg-[#0b1230]/85 p-6 shadow-xl sm:p-8"
      >
        <h2 class="text-xl font-semibold text-white">No Disruptive Activity</h2>
        <p class="mt-3 text-sm leading-relaxed text-white/75 sm:text-base">
          Aisslo cannot perform disruptive actions in your environment. Our AI has one
          job: generating certificates. It has no access to your desktops, servers, or
          internal systems.
        </p>

        <div class="mt-6 grid gap-4 sm:grid-cols-2">
          <div class="rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-5">
            <h3 class="flex items-center gap-2 text-base font-semibold text-emerald-300">
              <span aria-hidden="true">✓</span>
              What our AI does
            </h3>
            <ul class="mt-3 space-y-2 text-sm text-white/85 sm:text-base">
              <li>Generates certificates</li>
            </ul>
          </div>

          <div class="rounded-lg border border-red-500/40 bg-red-950/20 p-5">
            <h3 class="flex items-center gap-2 text-base font-semibold text-red-300">
              <span aria-hidden="true">✕</span>
              What it never does
            </h3>
            <ul class="mt-3 space-y-2 text-sm text-white/85 sm:text-base">
              <li class="font-semibold text-white">Access your desktops or machines</li>
              <li>Run commands or install software on your systems</li>
              <li>Change configurations, restart services, or modify files</li>
            </ul>
          </div>
        </div>

        <p class="mt-5 text-sm leading-relaxed text-white/60">
          Because Aisslo has no access to your machines, nothing it does can interrupt
          your services. Installing and deploying certificates stays fully in your
          team's hands.
        </p>
      </section>

      <section
        class="mb-6 rounded-xl border border-white/15 bg-[#0b1230]/85 p-6 shadow-xl sm:p-8"
      >
        <h2 class="text-xl font-semibold text-white">How We Calculate Risk</h2>
        <p class="mt-3 text-sm leading-relaxed text-white/75 sm:text-base">
          Every domain we monitor is given a risk level so you know what needs attention
          first. The level is based on two things only: how many days are left before the
          certificate expires, and whether the domain still accepts outdated TLS versions
          (TLS 1.0 or TLS 1.1). The rules are checked from most severe to least severe —
          the first rule that matches decides the level.
        </p>

        <ul class="mt-5 space-y-3">
          <li
            class="flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-950/20 px-4 py-3 text-sm sm:text-base"
          >
            <span class="mt-0.5 font-semibold text-red-300" aria-hidden="true">●</span>
            <span>
              <strong class="font-medium text-red-300">Critical</strong>
              <span class="block text-white/75">
                The certificate has already expired, or it expires within the next
                14 days. Visitors may already be seeing browser warnings, or will very
                soon. Renew immediately.
              </span>
            </span>
          </li>
          <li
            class="flex items-start gap-3 rounded-lg border border-orange-500/40 bg-orange-950/20 px-4 py-3 text-sm sm:text-base"
          >
            <span class="mt-0.5 font-semibold text-orange-300" aria-hidden="true">●</span>
            <span>
              <strong class="font-medium text-orange-300">High</strong>
              <span class="block text-white/75">
                The certificate expires within 30 days, <em>or</em> the domain still
                supports TLS 1.0 / TLS 1.1. Outdated TLS versions are deprecated and
                flagged by most compliance standards, so a domain is rated High for that
                reason alone — even if its certificate is valid for a long time.
              </span>
            </span>
          </li>
          <li
            class="flex items-start gap-3 rounded-lg border border-amber-400/40 bg-amber-950/20 px-4 py-3 text-sm sm:text-base"
          >
            <span class="mt-0.5 font-semibold text-amber-300" aria-hidden="true">●</span>
            <span>
              <strong class="font-medium text-amber-300">Medium</strong>
              <span class="block text-white/75">
                The certificate expires within 90 days and no outdated TLS versions were
                found. Nothing is broken — this is your reminder to schedule the renewal.
              </span>
            </span>
          </li>
          <li
            class="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-950/20 px-4 py-3 text-sm sm:text-base"
          >
            <span class="mt-0.5 font-semibold text-emerald-300" aria-hidden="true">●</span>
            <span>
              <strong class="font-medium text-emerald-300">Low</strong>
              <span class="block text-white/75">
                The certificate is valid for more than 90 days and the domain accepts only
                modern TLS versions. No action needed — we keep watching it for you.
              </span>
            </span>
          </li>
        </ul>

        <p class="mt-5 text-sm leading-relaxed text-white/60">
          Risk is recalculated from the latest certificate we find for each domain, so a
          level can improve on its own once a certificate is renewed or outdated TLS
          support is switched off.
        </p>
      </section>

      <section
        class="mb-8 rounded-xl border border-white/15 bg-[#0b1230]/85 p-6 shadow-xl sm:p-8"
      >
        <h2 class="text-xl font-semibold text-white">Encryption Practices</h2>
        <p class="mt-3 text-sm leading-relaxed text-white/75 sm:text-base">
          We apply industry-standard protections across our platform without exposing you
          to unnecessary complexity.
        </p>

        <ul class="mt-5 space-y-3">
          <li
            class="flex items-start gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm sm:text-base"
          >
            <span class="mt-0.5 text-[#31a1f6]" aria-hidden="true">●</span>
            <span>
              <strong class="font-medium text-white">HTTPS enforced</strong>
              <span class="block text-white/75">
                All communication with Aisslo is encrypted in transit.
              </span>
            </span>
          </li>
          <li
            class="flex items-start gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm sm:text-base"
          >
            <span class="mt-0.5 text-[#31a1f6]" aria-hidden="true">●</span>
            <span>
              <strong class="font-medium text-white">Encrypted storage</strong>
              <span class="block text-white/75">
                Data at rest is stored using encrypted storage practices.
              </span>
            </span>
          </li>
        </ul>
      </section>

      <footer class="flex flex-col items-center gap-4 border-t border-white/10 pt-8 sm:flex-row sm:justify-between">
        <p class="text-center text-xs text-white/50 sm:text-left">
          Questions about security? Contact your Aisslo representative.
        </p>
        <button
          type="button"
          (click)="navigateToLogin()"
          class="h-10 rounded-md bg-[#31a1f6] px-6 text-sm font-medium text-white transition-colors hover:bg-[#2387d6]"
        >
          Back to Login
        </button>
      </footer>
    </div>
  </div>
</div>
